/**
 * Tees N Tales quick add.
 *
 * 1. Pick rows ([data-tnt-pick], in the cart drawer and on the cart page): the [data-tnt-add]
 *    button adds the row's chosen variant ([data-tnt-variant] select or hidden input).
 * 2. <tnt-pairs> ("Make it two", product page): keeps the total and the button label in step
 *    with the ticked rows, and adds every ticked row in one go.
 *
 * Adding goes through Shopify.actions.updateCart, so Horizon's cart drawer refreshes (and opens,
 * with "Auto-open cart drawer" on) by itself. Without standard actions it falls back to
 * /cart/add.js and then the cart page.
 */
import { formatMoney } from '@theme/money-formatting';

const ERROR_TIMEOUT = 6000;

/** @returns {string} */
function routesRoot() {
  const shopify = /** @type {any} */ (window.Shopify);
  return shopify?.routes?.root || '/';
}

/**
 * @param {string | number | undefined} id
 * @returns {string}
 */
function rawId(id) {
  return String(id ?? '').split('/').pop() ?? '';
}

/**
 * @param {unknown} error
 * @returns {string}
 */
function messageOf(error) {
  if (error instanceof Error && error.message) return error.message;
  return 'Sorry, that could not be added. Please try again.';
}

/**
 * @param {Element} scope
 * @param {string} text
 */
function showError(scope, text) {
  const box = scope.querySelector('[data-tnt-error]');
  if (!(box instanceof HTMLElement)) return;
  box.textContent = text;
  box.hidden = !text;
  if (text) {
    window.setTimeout(() => {
      if (box.textContent === text) box.hidden = true;
    }, ERROR_TIMEOUT);
  }
}

/**
 * @param {HTMLButtonElement} button
 * @param {boolean} busy
 */
function setBusy(button, busy) {
  button.disabled = busy;
  button.setAttribute('aria-busy', String(busy));
}

/**
 * Adds lines to the cart.
 * @param {{ id: string, quantity?: number }[]} lines
 * @param {'product' | 'cart'} context
 */
async function addLines(lines, context) {
  const shopify = /** @type {any} */ (window.Shopify);
  const actions = shopify?.actions;

  if (actions && typeof actions.updateCart === 'function') {
    const result = await actions.updateCart(
      { lines: lines.map((line) => ({ merchandiseId: rawId(line.id), quantity: line.quantity || 1 })) },
      { event: { context } }
    );
    const errors = result?.userErrors ?? [];
    if (errors.length) throw new Error(errors[0]?.message || '');

    // From the product page, show the cart (and its free-delivery note) straight away.
    if (context === 'product' && typeof actions.openCart === 'function') {
      Promise.resolve(actions.openCart()).catch(() => {});
    }
    return;
  }

  const response = await fetch(`${routesRoot()}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: lines.map((line) => ({ id: Number(rawId(line.id)), quantity: line.quantity || 1 })),
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status) throw new Error(data.description || data.message || '');
  window.location.assign(`${routesRoot()}cart`);
}

/* ---------- Pick rows: one variant, one button ---------- */

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('[data-tnt-add]');
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  const row = button.closest('[data-tnt-pick]');
  const input = row?.querySelector('[data-tnt-variant]');
  if (!row || !(input instanceof HTMLInputElement || input instanceof HTMLSelectElement) || !input.value) return;

  event.preventDefault();
  setBusy(button, true);
  showError(row, '');

  try {
    await addLines([{ id: input.value, quantity: 1 }], button.dataset.context === 'product' ? 'product' : 'cart');
  } catch (error) {
    showError(row, messageOf(error));
  } finally {
    // The cart re-renders after a successful add, so the row is usually gone by now.
    if (button.isConnected) setBusy(button, false);
  }
});

// A size change updates the price shown on that row.
document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-tnt-variant]')) return;

  const row = select.closest('[data-tnt-pick], [data-tnt-row]');
  const label = row?.querySelector('[data-tnt-price]');
  const option = select.selectedOptions[0];
  if (label && option?.dataset.priceLabel) label.textContent = option.dataset.priceLabel;
});

/* ---------- Make it two ---------- */

class TntPairs extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    this.addEventListener('change', this.#refresh);
    this.addEventListener('click', this.#onClick);
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener('shopify:product:select', this.#onProductSelect);
    this.#refresh();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#refresh);
    this.removeEventListener('click', this.#onClick);
    this.#section?.removeEventListener('shopify:product:select', this.#onProductSelect);
    this.#section = null;
  }

  /**
   * The ticked rows, or with `ticked` false, the rows that could still be ticked.
   * @param {boolean} [ticked]
   * @returns {{ id: string, price: number }[]}
   */
  #collect(ticked = true) {
    /** @type {{ id: string, price: number }[]} */
    const lines = [];

    for (const row of this.querySelectorAll('[data-tnt-row]')) {
      const check = row.querySelector('[data-tnt-check]');
      if (!(check instanceof HTMLInputElement) || check.disabled || check.checked !== ticked) continue;

      const input = row.querySelector('[data-tnt-variant]');
      if (input instanceof HTMLSelectElement) {
        lines.push({ id: input.value, price: Number(input.selectedOptions[0]?.dataset.price || 0) });
      } else if (input instanceof HTMLInputElement && input.value) {
        lines.push({ id: input.value, price: Number(input.dataset.price || 0) });
      }
    }

    return lines;
  }

  /** @returns {{ id: string, price: number }[]} */
  get #lines() {
    return this.#collect(true);
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    const format = (this.dataset.moneyFormat || '{{amount}}').replace(/{{\s*amount\s*}}/, '{{amount_no_decimals}}');
    return formatMoney(cents, format, this.dataset.currency || 'PKR');
  }

  #refresh = () => {
    const lines = this.#lines;
    const count = lines.length;
    const total = lines.reduce((sum, line) => sum + line.price, 0);
    const threshold = Number(this.dataset.threshold || 0);

    // Order discount (e.g. 5% off from Rs 5,000): once the ticked total reaches it, show the discounted price
    const tierFrom = Number(this.dataset.tierFrom || 0);
    const tierPercent = Number(this.dataset.tierPercent || 0);
    const hasTier = tierFrom > 0 && tierPercent > 0;
    const tierReached = hasTier && count > 0 && total >= tierFrom;
    const payable = tierReached ? Math.round((total * (100 - tierPercent)) / 10000) * 100 : total;

    const button = this.querySelector('[data-tnt-pairs-add]');
    if (button instanceof HTMLButtonElement && button.getAttribute('aria-busy') !== 'true') {
      button.disabled = count === 0;
    }

    const label = this.querySelector('[data-tnt-label]');
    if (label) {
      const template = count === 1 ? this.dataset.labelOne : this.dataset.labelMany;
      label.textContent = count === 0 ? this.dataset.labelNone || '' : (template || '').replace('[count]', String(count));
    }

    const totalBox = this.querySelector('[data-tnt-total]');
    if (totalBox) {
      const parts = [];
      if (count) {
        parts.push(
          tierReached
            ? (this.dataset.tierTotalText || '[amount]')
                .replace('[amount]', this.#money(payable))
                .replace('[percent]', String(tierPercent))
            : (this.dataset.totalText || '[amount]').replace('[amount]', this.#money(total))
        );
        if (threshold > 0) {
          parts.push(
            payable >= threshold
              ? this.dataset.freeText || ''
              : (this.dataset.gapText || '').replace('[amount]', this.#money(threshold - payable))
          );
        }
        // Only when ticking the rest here would get there
        if (hasTier && !tierReached && this.dataset.tierMoreText) {
          const spare = this.#collect(false).reduce((sum, line) => sum + line.price, 0);
          if (total + spare >= tierFrom) {
            parts.push(
              this.dataset.tierMoreText
                .replace('[amount]', this.#money(tierFrom - total))
                .replace('[percent]', String(tierPercent))
            );
          }
        }
      }
      totalBox.textContent = parts.filter(Boolean).join(' · ');
    }
  };

  /** @param {Event} event */
  #onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-tnt-pairs-add]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const lines = this.#lines.filter((line) => line.id);
    if (!lines.length) return;

    setBusy(button, true);
    showError(this, '');

    try {
      await addLines(
        lines.map(({ id }) => ({ id, quantity: 1 })),
        'product'
      );
    } catch (error) {
      showError(this, messageOf(error));
    } finally {
      setBusy(button, false);
      this.#refresh();
    }
  };

  /**
   * A new size on the main product: swap in the rows Shopify rendered for it.
   * @param {Event} event
   */
  #onProductSelect = (event) => {
    const source = event.target instanceof Element ? event.target : null;
    if (!source || source.closest('product-card')) return;

    const promise = /** @type {any} */ (event).promise;
    if (!promise || typeof promise.then !== 'function') return;

    promise
      .then((/** @type {any} */ result) => {
        const html = result?.detail?.html;
        if (!html || typeof html.querySelector !== 'function') return;

        const next = html.querySelector(`tnt-pairs[data-block-id="${this.dataset.blockId}"]`);
        if (!(next instanceof Element)) return;

        this.innerHTML = next.innerHTML;
        this.hidden = next.hasAttribute('hidden');
        this.#refresh();
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[tnt-pairs] Could not refresh:', error);
      });
  };
}

if (!customElements.get('tnt-pairs')) {
  customElements.define('tnt-pairs', TntPairs);
}